import { useMutation, useQuery } from '@tanstack/react-query';
import { cplatApiV3 } from './axios';

// ============================================================
// Types
// ============================================================

export type SurveyQuestionType = 'RADIO' | 'CHECKBOX' | 'TEXTAREA';

export interface SurveyOption {
  optionId: number;
  optionText: string;
  orderIndex: number;
}

export interface SurveyQuestion {
  questionId: number;
  questionText: string;
  questionType: SurveyQuestionType;
  isRequired: boolean;
  orderIndex: number;
  options: SurveyOption[];
}

export interface PublicSurveyData {
  surveyId: number;
  title: string;
  description: string;
  backgroundImageUrl: string | null;
  headerImageUrl: string | null;
  submitButtonImageUrl: string | null;
  allowDuplicateResponse: boolean;
  questions: SurveyQuestion[];
}

interface PublicSurveyResponse {
  code: string;
  message: string;
  data: PublicSurveyData[];
}

// 응답 제출용 타입
export interface SurveyAnswerPayload {
  questionId: number;
  /**
   * 단일 선택형(RADIO) / 복수 선택형(CHECKBOX) 옵션 ID
   * - RADIO, CHECKBOX 모두 selectedOptionId 사용
   * - CHECKBOX의 경우 각 선택된 옵션마다 별도의 객체로 전송
   */
  selectedOptionId?: number;
  /** 주관식(TEXTAREA) 응답 텍스트 */
  answerText?: string;
}

export interface SubmitSurveyRequestPayload {
  cplatToken: string;
  answers: SurveyAnswerPayload[];
}

interface SubmitSurveyResponse {
  code: string;
  message: string;
  // BE에서 응답 body에 data를 내려줄 수 있으므로 확장 여지를 남겨둠
  data?: unknown;
}

// ============================================================
// API Functions
// ============================================================

/**
 * 공개용 설문조사 조회 API (인증 불필요)
 * GET /v3/surveys/{surveyId}
 */
export const getPublicSurvey = async (surveyId: number | string): Promise<PublicSurveyData> => {
  const res = await cplatApiV3.get<PublicSurveyResponse>(`/surveys/${surveyId}`);
  return res.data.data[0];
};

/**
 * 서베이 응답 제출 API
 * POST /v3/surveys/{surveyId}/responses
 *
 * Body:
 * {
 *   "cplatToken": "eyJhbGciOiJIUzI1NiIsIn...",
 *   "answers": [
 *     { "questionId": 1001, "selectedOptionId": 501 },
 *     { "questionId": 1001, "selectedOptionId": 502 },
 *     { "questionId": 1002, "answerText": "좋아요" }
 *   ]
 * }
 *
 * Note: CHECKBOX의 경우 각 선택된 옵션마다 별도의 객체로 전송
 */
export const submitSurveyResponse = async (
  surveyId: number | string,
  payload: SubmitSurveyRequestPayload,
): Promise<SubmitSurveyResponse> => {
  const res = await cplatApiV3.post<SubmitSurveyResponse>(`/surveys/${surveyId}/responses`, payload);
  return res.data;
};

// ============================================================
// Query Keys
// ============================================================

export const surveyKeys = {
  all: ['surveys'] as const,
  detail: (surveyId: number | string) => [...surveyKeys.all, 'detail', surveyId] as const,
};

// ============================================================
// React Query Hooks
// ============================================================

/**
 * 공개용 설문조사 조회 hook
 */
export const usePublicSurvey = (surveyId: number | string | undefined) => {
  return useQuery({
    queryKey: surveyKeys.detail(surveyId!),
    queryFn: () => getPublicSurvey(surveyId!),
    enabled: !!surveyId,
  });
};

/**
 * 서베이 응답 제출 mutation hook
 *
 * 사용 예:
 * const submitMutation = useSubmitSurveyResponse();
 * submitMutation.mutate({ surveyId, payload: { cplatToken, answers } });
 */
export const useSubmitSurveyResponse = () => {
  return useMutation({
    mutationFn: (params: { surveyId: number | string; payload: SubmitSurveyRequestPayload }) =>
      submitSurveyResponse(params.surveyId, params.payload),
  });
};
